/**
 * Odds Parser Utility - Bovada Format
 * Parses pasted odds data from Bovada betting sites
 */

import type { Fight, FightMarket, MarketOutcome, Fighter } from '../types';

export interface ParsedFightData {
  fighter1: string;
  fighter2: string;
  fighter1Odds: number;
  fighter2Odds: number;
  markets: FightMarket[];
  startDate?: string;
  startTime?: string;
}

/**
 * Parse American odds from string - handles +125, -165, or just 125, -165
 */
function parseAmericanOdds(oddsStr: string): number | null {
  if (!oddsStr) return null;
  
  // Clean the string - remove parentheses, extra spaces
  let cleaned = oddsStr.trim().replace(/[()]/g, '');
  
  // If it starts with + or -, parse it directly
  if (cleaned.match(/^[+-]\d+$/)) {
    const num = parseInt(cleaned, 10);
    return isNaN(num) ? null : num;
  }
  
  // If it's just a number, add + prefix for positive
  if (cleaned.match(/^\d+$/)) {
    const num = parseInt(cleaned, 10);
    if (isNaN(num)) return null;
    return num; // Return as positive (we'll add + when displaying)
  }
  
  // Try to extract any number with optional sign
  const match = cleaned.match(/([+-]?\d+)/);
  if (match) {
    const num = parseInt(match[1], 10);
    return isNaN(num) ? null : num;
  }
  
  return null;
}

/**
 * Extract fighters from header line
 * Format: "Jack Della Maddalena vs Islam Makhachev"
 */
function extractFighters(lines: string[]): { fighter1: string; fighter2: string } | null {
  for (const line of lines) {
    const vsMatch = line.match(/^(.+?)\s+vs\s+(.+?)$/i);
    if (vsMatch) {
      return {
        fighter1: vsMatch[1].trim(),
        fighter2: vsMatch[2].trim(),
      };
    }
  }
  return null;
}

/**
 * Extract moneyline odds - look for standalone odds lines near fighter names
 */
function extractMoneylineOdds(lines: string[]): {
  fighter1Odds: number;
  fighter2Odds: number;
} {
  let fighter1Odds = 0;
  let fighter2Odds = 0;
  
  // Look for lines with just odds (like "+210" and "-250")
  // These typically appear in the first 15 lines after the fighter names
  const oddsLines: number[] = [];
  
  for (let i = 0; i < Math.min(20, lines.length); i++) {
    const line = lines[i].trim();
    
    // Match standalone odds line
    if (line.match(/^[+-]\d+$/)) {
      const odds = parseAmericanOdds(line);
      if (odds !== null && oddsLines.length < 2) {
        oddsLines.push(odds);
      }
    }
  }
  
  // Assign in order they appear
  if (oddsLines.length >= 2) {
    fighter1Odds = oddsLines[0];
    fighter2Odds = oddsLines[1];
  }
  
  return { fighter1Odds, fighter2Odds };
}

/**
 * Parse Total Rounds Over/Under
 * Format can be:
 * - Vertical table: "Total Rounds Over/Under" header, then "Over", "Under", then round values, then over odds, then under odds
 * - Horizontal table: "0.5  -1800  +800" (round, over odds, under odds)
 * - Inline: "O3.5 (-130)" and "U3.5 (EVEN)"
 */
function parseTotals(lines: string[]): MarketOutcome[] {
  const outcomes: MarketOutcome[] = [];
  const seen = new Set<string>();
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Check if we're in the Total Rounds section
    if (line.toLowerCase().includes('total rounds over/under')) {
      console.log('Found Total Rounds section at line', i);
      
      // Collect all subsequent lines until we hit another section
      const sectionLines: string[] = [];
      for (let j = i + 1; j < Math.min(i + 50, lines.length); j++) {
        const dataLine = lines[j].trim();
        
        // End of section
        if (dataLine.toLowerCase().includes('round betting') ||
            dataLine.toLowerCase().includes('fight props') ||
            dataLine.toLowerCase().includes('method of victory') ||
            dataLine.toLowerCase().includes('fight winner')) {
          break;
        }
        
        if (dataLine.length > 0) {
          sectionLines.push(dataLine);
        }
      }
      
      console.log('Section lines:', sectionLines);
      
      // Try vertical format first
      // Format: "Over", "Under", then round values, then over odds, then under odds
      const roundValues: number[] = [];
      const overOdds: number[] = [];
      const underOdds: number[] = [];
      
      let inRoundValues = false;
      let inOverOdds = false;
      
      for (const dataLine of sectionLines) {
        // Skip header lines
        if (dataLine.toLowerCase() === 'over') {
          inRoundValues = true;
          continue;
        }
        if (dataLine.toLowerCase() === 'under') {
          continue;
        }
        
        // Check if this is a round value (0.5, 1.5, 2.5, etc.)
        const roundVal = parseFloat(dataLine);
        if (!isNaN(roundVal) && roundVal > 0 && roundVal < 10 && inRoundValues && !inOverOdds) {
          roundValues.push(roundVal);
          continue;
        }
        
        // Check if this is an odds value (starts with + or -)
        const odds = parseAmericanOdds(dataLine);
        if (odds !== null) {
          if (!inOverOdds) {
            // First set of odds are "Over" odds
            inOverOdds = true;
            overOdds.push(odds);
          } else if (overOdds.length < roundValues.length) {
            // Still collecting over odds
            overOdds.push(odds);
          } else {
            // Now collecting under odds
            underOdds.push(odds);
          }
          continue;
        }
        
        // Try horizontal format: "2.5    -205    +155"
        const parts = dataLine.split(/\s+/);
        if (parts.length >= 3) {
          const rv = parseFloat(parts[0]);
          if (!isNaN(rv)) {
            const oo = parseAmericanOdds(parts[1]);
            const uo = parseAmericanOdds(parts[2]);
            
            if (oo !== null && uo !== null) {
              const overKey = `over_${rv}`;
              if (!seen.has(overKey)) {
                seen.add(overKey);
                outcomes.push({
                  id: `outcome-${Date.now()}-${Math.random()}`,
                  name: `Over ${rv}`,
                  odds: oo,
                });
              }
              
              const underKey = `under_${rv}`;
              if (!seen.has(underKey)) {
                seen.add(underKey);
                outcomes.push({
                  id: `outcome-${Date.now()}-${Math.random()}`,
                  name: `Under ${rv}`,
                  odds: uo,
                });
              }
            }
          }
        }
      }
      
      // Process vertical format results
      console.log('Round values:', roundValues);
      console.log('Over odds:', overOdds);
      console.log('Under odds:', underOdds);
      
      if (roundValues.length > 0 && roundValues.length === overOdds.length && roundValues.length === underOdds.length) {
        for (let k = 0; k < roundValues.length; k++) {
          const rv = roundValues[k];
          const oo = overOdds[k];
          const uo = underOdds[k];
          
          const overKey = `over_${rv}`;
          if (!seen.has(overKey)) {
            seen.add(overKey);
            outcomes.push({
              id: `outcome-${Date.now()}-${Math.random()}`,
              name: `Over ${rv}`,
              odds: oo,
            });
          }
          
          const underKey = `under_${rv}`;
          if (!seen.has(underKey)) {
            seen.add(underKey);
            outcomes.push({
              id: `outcome-${Date.now()}-${Math.random()}`,
              name: `Under ${rv}`,
              odds: uo,
            });
          }
        }
      }
    }
    
    // Also handle inline format: "O3.5 (-130)" or "U3.5 (EVEN)"
    const overMatch = line.match(/O(\d+\.?\d*)\s*\(([+-]?\w+)\)/i);
    const underMatch = line.match(/U(\d+\.?\d*)\s*\(([+-]?\w+)\)/i);
    
    if (overMatch) {
      const roundVal = overMatch[1];
      const oddsStr = overMatch[2] === 'EVEN' ? '+100' : overMatch[2];
      const odds = parseAmericanOdds(oddsStr);
      if (odds !== null) {
        const key = `over_${roundVal}`;
        if (!seen.has(key)) {
          seen.add(key);
          outcomes.push({
            id: `outcome-${Date.now()}-${Math.random()}`,
            name: `Over ${roundVal}`,
            odds,
          });
        }
      }
    }
    
    if (underMatch) {
      const roundVal = underMatch[1];
      const oddsStr = underMatch[2] === 'EVEN' ? '+100' : underMatch[2];
      const odds = parseAmericanOdds(oddsStr);
      if (odds !== null) {
        const key = `under_${roundVal}`;
        if (!seen.has(key)) {
          seen.add(key);
          outcomes.push({
            id: `outcome-${Date.now()}-${Math.random()}`,
            name: `Under ${roundVal}`,
            odds,
          });
        }
      }
    }
  }
  
  return outcomes;
}

/**
 * Parse any line with fighter name + description + odds
 * Format: "Jack Della Maddalena Wins by KO, TKO or DQ+400"
 * Format: "Jack Della Maddalena by TKO/KO/DQ in Round 1+1200"
 */
function parseGenericFighterBet(line: string, fighter1: string, fighter2: string): MarketOutcome | null {
  // Extract odds from end of line (must end with +/- followed by digits)
  const oddsMatch = line.match(/([+-]\d+)$/);
  if (!oddsMatch) return null;
  
  const odds = parseAmericanOdds(oddsMatch[1]);
  if (odds === null) return null;
  
  // Extract the outcome name (everything before the odds)
  const outcomeName = line.substring(0, line.lastIndexOf(oddsMatch[1])).trim();
  
  // Must contain a fighter name
  const lineLower = line.toLowerCase();
  if (!lineLower.includes(fighter1.toLowerCase()) && !lineLower.includes(fighter2.toLowerCase())) {
    return null;
  }
  
  if (!outcomeName || outcomeName.length === 0) return null;
  
  return {
    id: `outcome-${Date.now()}-${Math.random()}`,
    name: outcomeName,
    odds,
  };
}

/**
 * Parse Method of Victory props (all variants)
 */
function parseMethodOfVictory(lines: string[], fighter1: string, fighter2: string): MarketOutcome[] {
  const outcomes: MarketOutcome[] = [];
  const seen = new Set<string>();
  
  let inMethodSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineLower = line.toLowerCase();
    
    // Detect any method of victory section (including alternates)
    if (lineLower.includes('method of victory') || 
        lineLower === 'double chance' ||
        lineLower.includes('round and method of victory')) {
      inMethodSection = true;
      continue;
    }
    
    // End section if we hit a major new heading
    if (inMethodSection && (
      lineLower === 'round props' ||
      lineLower === 'fight props' ||
      lineLower === 'total rounds over/under' ||
      lineLower === 'round betting' ||
      lineLower === 'alternate round betting' ||
      lineLower === 'fight winner & total rounds' ||
      lineLower === 'when will the fight end' ||
      lineLower === 'winning minute' ||
      lineLower === 'squares' ||
      lineLower === 'bout specials'
    )) {
      inMethodSection = false;
    }
    
    // Skip section description lines
    if (lineLower.includes('both must fight for action') || 
        lineLower.includes('bets void if')) {
      continue;
    }
    
    if (inMethodSection) {
      const outcome = parseGenericFighterBet(line, fighter1, fighter2);
      if (outcome) {
        const key = outcome.name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          outcomes.push(outcome);
        }
      }
    }
    
    // Also catch "Wins by" lines outside sections
    if (!inMethodSection && (lineLower.includes('wins by') || lineLower.includes('wins inside distance'))) {
      const outcome = parseGenericFighterBet(line, fighter1, fighter2);
      if (outcome) {
        const key = outcome.name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          outcomes.push(outcome);
        }
      }
    }
  }
  
  return outcomes;
}

/**
 * Parse Round Props - includes round betting and round+method combos
 */
function parseRoundProps(lines: string[], fighter1: string, fighter2: string): MarketOutcome[] {
  const outcomes: MarketOutcome[] = [];
  const seen = new Set<string>();
  
  let inRoundSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineLower = line.toLowerCase();
    
    // Detect round section (including Fight Winner & Total Rounds, When Will Fight End)
    if (lineLower === 'round betting' || 
        lineLower === 'round props' ||
        lineLower === 'alternate round betting' ||
        lineLower.includes('alternate round betting') ||
        lineLower === 'fight winner & total rounds' ||
        lineLower === 'when will the fight end' ||
        lineLower === 'fight to complete') {
      inRoundSection = true;
      continue;
    }
    
    // End section if we hit major new heading
    if (inRoundSection && (
      lineLower === 'fight props' ||
      lineLower === 'winning minute' ||
      lineLower === 'squares' ||
      lineLower === 'bout specials' ||
      lineLower === 'method of victory props'
    )) {
      inRoundSection = false;
    }
    
    // Skip description lines
    if (lineLower.includes('both must fight for action')) {
      continue;
    }
    
    // Parse if in section
    if (inRoundSection) {
      const outcome = parseGenericFighterBet(line, fighter1, fighter2);
      if (outcome) {
        const key = outcome.name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          outcomes.push(outcome);
        }
      }
      
      // Also parse generic round outcomes (no fighter name)
      // Like "Round 1+400" or "By Decision-225"
      const roundOnlyMatch = line.match(/^(Round\s+\d+|By\s+Decision|Draw)([+-]\d+)$/i);
      if (roundOnlyMatch) {
        const name = roundOnlyMatch[1];
        const odds = parseAmericanOdds(roundOnlyMatch[2]);
        if (odds !== null) {
          const key = name.toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            outcomes.push({
              id: `outcome-${Date.now()}-${Math.random()}`,
              name,
              odds,
            });
          }
        }
      }
    }
  }
  
  return outcomes;
}

/**
 * Parse Fight Props section
 */
function parseFightProps(lines: string[], fighter1: string, fighter2: string): MarketOutcome[] {
  const outcomes: MarketOutcome[] = [];
  const seen = new Set<string>();
  
  let inFightPropsSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineLower = line.toLowerCase();
    
    // Start Fight Props section
    if (lineLower === 'fight props') {
      inFightPropsSection = true;
      continue;
    }
    
    // End Fight Props section
    if (inFightPropsSection && (
      lineLower === 'winning minute' ||
      lineLower === 'squares' ||
      lineLower === 'bout specials' ||
      lineLower === 'method of victory props' ||
      lineLower === 'round props'
    )) {
      inFightPropsSection = false;
    }
    
    // Skip description lines
    if (lineLower.includes('bets void if') || 
        lineLower.includes('both must fight for action')) {
      continue;
    }
    
    if (inFightPropsSection) {
      // "Will The Fight Go The Distance" - Yes/No format
      if (lineLower.includes('go') && lineLower.includes('distance')) {
        // Check next 3 lines for Yes/No
        for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
          const nextLine = lines[j].trim();
          const yesNoMatch = nextLine.match(/^(Yes|No)([+-]\d+)$/i);
          if (yesNoMatch) {
            const yesNo = yesNoMatch[1];
            const odds = parseAmericanOdds(yesNoMatch[2]);
            if (odds !== null) {
              const name = `Fight Go Distance: ${yesNo}`;
              const key = name.toLowerCase();
              if (!seen.has(key)) {
                seen.add(key);
                outcomes.push({
                  id: `outcome-${Date.now()}-${Math.random()}`,
                  name,
                  odds,
                });
              }
            }
          }
        }
        continue;
      }
      
      // "Fight Winner - Inside The Distance Only" (fighter-specific)
      if (lineLower.includes('inside the distance only')) {
        // Check previous line for section header
        // Check next lines for fighters
        for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
          const nextLine = lines[j].trim();
          const outcome = parseGenericFighterBet(nextLine, fighter1, fighter2);
          if (outcome) {
            // Add "Inside Distance Only" to name for clarity
            const enhancedName = `${outcome.name} (Inside Distance Only)`;
            const key = enhancedName.toLowerCase();
            if (!seen.has(key)) {
              seen.add(key);
              outcomes.push({
                id: outcome.id,
                name: enhancedName,
                odds: outcome.odds,
              });
            }
          }
        }
        continue;
      }
      
      // "Fight To Complete X Full Rounds" - Yes/No format
      if (lineLower.includes('fight to complete') && lineLower.includes('full round')) {
        const roundMatch = line.match(/complete\s+(\d+)\s+full\s+round/i);
        if (roundMatch) {
          const round = roundMatch[1];
          // Check next 3 lines for Yes/No
          for (let j = i + 1; j < Math.min(i + 4, lines.length); j++) {
            const nextLine = lines[j].trim();
            const yesNoMatch = nextLine.match(/^(Yes|No)\s*\([^)]*\)([+-]\d+)$/i);
            if (yesNoMatch) {
              const yesNo = yesNoMatch[1];
              const odds = parseAmericanOdds(yesNoMatch[2]);
              if (odds !== null) {
                const name = `Complete ${round} Rounds: ${yesNo}`;
                const key = name.toLowerCase();
                if (!seen.has(key)) {
                  seen.add(key);
                  outcomes.push({
                    id: `outcome-${Date.now()}-${Math.random()}`,
                    name,
                    odds,
                  });
                }
              }
            }
          }
        }
      }
    }
  }
  
  return outcomes;
}

/**
 * Parse "How Will Fight End" section
 */
function parseHowWillFightEnd(lines: string[]): MarketOutcome[] {
  const outcomes: MarketOutcome[] = [];
  const seen = new Set<string>();
  
  let inHowWillSection = false;
  
  for (const line of lines) {
    const lineLower = line.toLowerCase();
    
    if (lineLower.includes('how will fight end')) {
      inHowWillSection = true;
      continue;
    }
    
    if (inHowWillSection && (
      lineLower.includes('round props') ||
      lineLower.includes('fight props') ||
      lineLower.includes('double chance')
    )) {
      inHowWillSection = false;
    }
    
    if (inHowWillSection) {
      const oddsMatch = line.match(/([+-]\d+)$/);
      if (oddsMatch) {
        const odds = parseAmericanOdds(oddsMatch[1]);
        if (odds !== null) {
          const name = line.substring(0, line.lastIndexOf(oddsMatch[1])).trim();
          if (name.length > 0) {
            const key = name.toLowerCase();
            if (!seen.has(key)) {
              seen.add(key);
              outcomes.push({
                id: `outcome-${Date.now()}-${Math.random()}`,
                name,
                odds,
              });
            }
          }
        }
      }
    }
  }
  
  return outcomes;
}

/**
 * Parse Winning Minute
 * Bet on which minute of which round the fight ends
 */
function parseWinningMinute(lines: string[]): MarketOutcome[] {
  const outcomes: MarketOutcome[] = [];
  const seen = new Set<string>();
  let inWinningMinuteSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineLower = line.toLowerCase();
    
    // Start of Winning Minute section
    if (lineLower === 'winning minute') {
      inWinningMinuteSection = true;
      continue;
    }
    
    // End of section
    if (inWinningMinuteSection && (
      lineLower === 'squares' ||
      lineLower.includes('squares game') ||
      lineLower === 'bout specials' ||
      lineLower === 'method of victory props'
    )) {
      inWinningMinuteSection = false;
      break;
    }
    
    // Skip description lines
    if (lineLower.includes('what minute will the fight end') ||
        lineLower.includes('if the fight goes the distance')) {
      continue;
    }
    
    if (inWinningMinuteSection) {
      // Match patterns like "1st Minute of Round 1+3500"
      const matches = line.matchAll(/(\d+(?:st|nd|rd|th))\s+Minute\s+of\s+Round\s+(\d+)([+-]\d+)/gi);
      for (const match of matches) {
        const minute = match[1];
        const round = match[2];
        const oddsStr = match[3];
        const odds = parseAmericanOdds(oddsStr);
        
        const key = `${minute}-round-${round}`;
        if (odds !== null && !seen.has(key)) {
          seen.add(key);
          outcomes.push({
            id: `winning-minute-${minute}-${round}`,
            name: `${minute} Minute of Round ${round}`,
            odds,
          });
        }
      }
    }
  }
  
  return outcomes;
}

/**
 * Parse Fight Winner & Total Rounds (combined bets)
 * Both conditions must be met (e.g., "Valentina & Over 2.5 Rounds")
 */
function parseFightWinnerAndTotalRounds(lines: string[], fighter1: string, fighter2: string): MarketOutcome[] {
  const outcomes: MarketOutcome[] = [];
  const seen = new Set<string>();
  let inSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineLower = line.toLowerCase();
    
    // Start of section
    if (lineLower.includes('fight winner & total rounds') || lineLower.includes('fighter & total rounds')) {
      inSection = true;
      continue;
    }
    
    // End of section
    if (inSection && (
      lineLower.includes('when will') ||
      lineLower.includes('fight to complete') ||
      lineLower === ''
    )) {
      break;
    }
    
    if (inSection) {
      // Match patterns like "Valentina Shevchenko & Over 2.5 Rounds-115"
      // Try fighter 1
      const fighter1Match = line.match(new RegExp(`${fighter1.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*&\\s*(Over|Under)\\s+(\\d+\\.?\\d*)\\s*Rounds([+-]?\\w+)`, 'i'));
      if (fighter1Match) {
        const overUnder = fighter1Match[1];
        const rounds = fighter1Match[2];
        const oddsStr = fighter1Match[3] === 'EVEN' ? '+100' : fighter1Match[3];
        const odds = parseAmericanOdds(oddsStr);
        
        const key = `${fighter1}-${overUnder}-${rounds}`;
        if (odds !== null && !seen.has(key)) {
          seen.add(key);
          outcomes.push({
            id: `fighter-rounds-${Date.now()}-${Math.random()}`,
            name: `${fighter1} & ${overUnder} ${rounds} Rounds`,
            odds,
          });
        }
      }
      
      // Try fighter 2
      const fighter2Match = line.match(new RegExp(`${fighter2.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*&\\s*(Over|Under)\\s+(\\d+\\.?\\d*)\\s*Rounds([+-]?\\w+)`, 'i'));
      if (fighter2Match) {
        const overUnder = fighter2Match[1];
        const rounds = fighter2Match[2];
        const oddsStr = fighter2Match[3] === 'EVEN' ? '+100' : fighter2Match[3];
        const odds = parseAmericanOdds(oddsStr);
        
        const key = `${fighter2}-${overUnder}-${rounds}`;
        if (odds !== null && !seen.has(key)) {
          seen.add(key);
          outcomes.push({
            id: `fighter-rounds-${Date.now()}-${Math.random()}`,
            name: `${fighter2} & ${overUnder} ${rounds} Rounds`,
            odds,
          });
        }
      }
    }
  }
  
  return outcomes;
}

/**
 * Parse Squares Game (KO/TKO/Submission)
 * Bets on last two digits of finishing time (00-59)
 */
function parseSquares(lines: string[]): MarketOutcome[] {
  const outcomes: MarketOutcome[] = [];
  const seen = new Set<string>();
  let inSquaresSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineLower = line.toLowerCase();
    
    // Start of Squares section
    if (lineLower.includes('squares game') || (lineLower === 'squares' && i + 1 < lines.length && lines[i + 1].toLowerCase().includes('squares game'))) {
      inSquaresSection = true;
      continue;
    }
    
    // End of Squares section (next major section or empty line)
    if (inSquaresSection && (
      lineLower.includes('method of victory') ||
      lineLower.includes('round props') ||
      lineLower.includes('fight props') ||
      lineLower === ''
    )) {
      break;
    }
    
    if (inSquaresSection) {
      // Match pattern like "00+5000", "01+5000", etc.
      // Each number 00-59 with its odds
      const matches = line.matchAll(/(\d{2})([+-]\d+)/g);
      for (const match of matches) {
        const number = match[1];
        const oddsStr = match[2];
        const odds = parseAmericanOdds(oddsStr);
        
        if (odds !== null && !seen.has(number)) {
          seen.add(number);
          outcomes.push({
            id: `squares-${number}`,
            name: number,
            odds,
          });
        }
      }
    }
  }
  
  return outcomes;
}

/**
 * Parse Bout Specials (Fight Specials)
 * Complex multi-condition bets with AND/OR logic
 */
function parseBoutSpecials(lines: string[]): MarketOutcome[] {
  const outcomes: MarketOutcome[] = [];
  const seen = new Set<string>();
  let inBoutSpecialsSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineLower = line.toLowerCase();
    
    // Start of Bout Specials section
    if (lineLower.includes('bout specials') || lineLower.includes('fight specials')) {
      inBoutSpecialsSection = true;
      continue;
    }
    
    // End of section
    if (inBoutSpecialsSection && (
      lineLower.includes('method of victory') ||
      lineLower.includes('round props') ||
      lineLower === ''
    )) {
      break;
    }
    
    if (inBoutSpecialsSection) {
      // Match pattern: "Description+100" or "DescriptionEVEN" or "Description-150"
      const oddsMatch = line.match(/([+-]\d+|EVEN)$/);
      if (oddsMatch) {
        const oddsStr = oddsMatch[1] === 'EVEN' ? '+100' : oddsMatch[1];
        const odds = parseAmericanOdds(oddsStr);
        
        if (odds !== null) {
          const name = line.substring(0, line.lastIndexOf(oddsMatch[1])).trim();
          
          if (name.length > 0 && !seen.has(name.toLowerCase())) {
            seen.add(name.toLowerCase());
            outcomes.push({
              id: `bout-special-${Date.now()}-${Math.random()}`,
              name,
              odds,
            });
          }
        }
      }
    }
  }
  
  return outcomes;
}

/**
 * Main parser function
 */
export function parseOddsText(text: string): ParsedFightData | null {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  
  if (lines.length === 0) return null;
  
  // Extract fighters
  const fighters = extractFighters(lines);
  if (!fighters) return null;
  
  // Extract moneyline odds
  const { fighter1Odds, fighter2Odds } = extractMoneylineOdds(lines);
  
  // Build markets
  const markets: FightMarket[] = [];
  
  // Moneyline market
  if (fighter1Odds !== 0 && fighter2Odds !== 0) {
    markets.push({
      key: 'moneyline',
      name: 'Moneyline',
      outcomes: [
        {
          id: `moneyline-${fighters.fighter1}`,
          name: fighters.fighter1,
          odds: fighter1Odds,
        },
        {
          id: `moneyline-${fighters.fighter2}`,
          name: fighters.fighter2,
          odds: fighter2Odds,
        },
      ],
    });
  }
  
  // Total Rounds
  const totals = parseTotals(lines);
  if (totals.length > 0) {
    markets.push({
      key: 'total_rounds',
      name: 'Total Rounds',
      outcomes: totals,
    });
  }
  
  // Method of Victory
  const methodOutcomes = parseMethodOfVictory(lines, fighters.fighter1, fighters.fighter2);
  if (methodOutcomes.length > 0) {
    markets.push({
      key: 'method_of_victory',
      name: 'Method of Victory',
      outcomes: methodOutcomes,
    });
  }
  
  // Round Props
  const roundOutcomes = parseRoundProps(lines, fighters.fighter1, fighters.fighter2);
  if (roundOutcomes.length > 0) {
    markets.push({
      key: 'round_props',
      name: 'Round Props',
      outcomes: roundOutcomes,
    });
  }
  
  // How Will Fight End
  const howWillEnd = parseHowWillFightEnd(lines);
  if (howWillEnd.length > 0) {
    markets.push({
      key: 'how_will_fight_end',
      name: 'How Will Fight End',
      outcomes: howWillEnd,
    });
  }
  
  // Fight Props
  const fightProps = parseFightProps(lines, fighters.fighter1, fighters.fighter2);
  if (fightProps.length > 0) {
    markets.push({
      key: 'fight_props',
      name: 'Fight Props',
      outcomes: fightProps,
    });
  }
  
  // Winning Minute
  const winningMinute = parseWinningMinute(lines);
  if (winningMinute.length > 0) {
    markets.push({
      key: 'winning_minute',
      name: 'Winning Minute',
      outcomes: winningMinute,
    });
  }
  
  // Fight Winner & Total Rounds
  const fightWinnerRounds = parseFightWinnerAndTotalRounds(lines, fighters.fighter1, fighters.fighter2);
  if (fightWinnerRounds.length > 0) {
    markets.push({
      key: 'fighter_and_total_rounds',
      name: 'Fight Winner & Total Rounds',
      outcomes: fightWinnerRounds,
    });
  }
  
  // Squares Game
  const squares = parseSquares(lines);
  if (squares.length > 0) {
    markets.push({
      key: 'squares_game',
      name: 'Squares Game (KO/TKO/Submission)',
      outcomes: squares,
    });
  }
  
  // Bout Specials
  const boutSpecials = parseBoutSpecials(lines);
  if (boutSpecials.length > 0) {
    markets.push({
      key: 'bout_specials',
      name: 'Bout Specials',
      outcomes: boutSpecials,
    });
  }
  
  return {
    fighter1: fighters.fighter1,
    fighter2: fighters.fighter2,
    fighter1Odds,
    fighter2Odds,
    markets,
  };
}

/**
 * Create a Fight object from parsed data
 */
export function createFightFromParsedData(
  eventId: string,
  parsed: ParsedFightData
): Fight {
  const fightId = `fight-${Date.now()}`;
  
  const fighters: Fighter[] = [
    {
      id: `fighter-${fightId}-1`,
      name: parsed.fighter1,
      odds: parsed.fighter1Odds,
    },
    {
      id: `fighter-${fightId}-2`,
      name: parsed.fighter2,
      odds: parsed.fighter2Odds,
    },
  ];
  
  // Ensure there's always at least a moneyline market
  let markets = parsed.markets;
  if (markets.length === 0 || !markets.find(m => m.key === 'moneyline')) {
    // Add moneyline from fighters if missing
    const moneylineMarket: FightMarket = {
      key: 'moneyline',
      name: 'Moneyline',
      outcomes: fighters.map(f => ({
        id: `moneyline-${f.name}`,
        name: f.name,
        odds: f.odds,
      })),
    };
    markets = [moneylineMarket, ...markets];
  }

  return {
    id: fightId,
    eventId,
    fighters,
    status: 'upcoming',
    markets,
  };
}
